import { Subject } from 'rxjs';
import { numbers } from './numbers';
import {CategoryStatistic, CategoryStatistics} from "../DataModels/Statistics";

export class Questionnaire {
    // questions and answers
    private _currentQuestion: Questions = Questions.calories;
    get currentQuestion(): Questions { return this._currentQuestion; }
    set currentQuestion(value: Questions) {
        this._currentQuestion = value; this.changedSubject.next({type: QuestionnaireChangeType.miscellaneous});
    }
    questionOrder: QuestionOrder = new QuestionOrder(this);
    type: QuestionnaireType = QuestionnaireType.standard;
    gender: number;
    size: number;
    weight: number;
    age: number;
    activityLevel: number;
    private _generalNutritionType: number;
    get generalNutritionType(): number {return this._generalNutritionType; }
    set generalNutritionType(value: number) {this._generalNutritionType = value; this.calculateNutritionPercentages(); }
    private _eggsAnswer: number;
    get eggsAnswer(): number {return this._eggsAnswer; }
    set eggsAnswer(value: number) {this._eggsAnswer = value; this.calculateNutritionPercentages(); }
    private _cheeseAnswer: number;
    get cheeseAnswer(): number {return this._cheeseAnswer; }
    set cheeseAnswer(value: number) {this._cheeseAnswer = value; this.calculateNutritionPercentages(); }
    private _dairyAnswer: number;
    get dairyAnswer(): number {return this._dairyAnswer; }
    set dairyAnswer(value: number) {this._dairyAnswer = value; this.calculateNutritionPercentages(); }
    private _meatAnswer: number;
    get meatAnswer(): number {return this._meatAnswer; }
    set meatAnswer(value: number) {this._meatAnswer = value; this.calculateNutritionPercentages(); }
    private _fishAnswer: number;
    get fishAnswer(): number {return this._fishAnswer; }
    set fishAnswer(value: number) {this._fishAnswer = value; this.calculateNutritionPercentages(); }
    private _fruitsAnswer: number = StandardAnswers.nowAndThen;
    get fruitsAnswer(): number {return this._fruitsAnswer; }
    set fruitsAnswer(value: number) {this._fruitsAnswer = value; this.calculateNutritionPercentages(); }
    private _vegetablesAnswer: number = StandardAnswers.nowAndThen;
    get vegetablesAnswer(): number {return this._vegetablesAnswer; }
    set vegetablesAnswer(value: number) {this._vegetablesAnswer = value; this.calculateNutritionPercentages(); }
    private _grainAnswer: number = StandardAnswers.nowAndThen;
    get grainAnswer(): number {return this._grainAnswer; }
    set grainAnswer(value: number) {this._grainAnswer = value; this.calculateNutritionPercentages(); }
    private _nutsAnswer: number;
    get nutsAnswer(): number {return this._nutsAnswer; }
    set nutsAnswer(value: number) {this._nutsAnswer = value; this.calculateNutritionPercentages(); }
    private _frozenFoodAnswer: number;
    get frozenFoodAnswer(): number {return this._frozenFoodAnswer; }
    set frozenFoodAnswer(value: number) {this._frozenFoodAnswer = value; this.calculateNutritionPercentages(); }
    private _sweetsAnswer: number;
    get sweetsAnswer(): number {return this._sweetsAnswer; }
    set sweetsAnswer(value: number) {this._sweetsAnswer = value; this.calculateNutritionPercentages(); }
    private _foodWasteAnswer: number;
    get foodWasteAnswer(): number {return this._foodWasteAnswer; }
    set foodWasteAnswer(value: number) {this._foodWasteAnswer = value; this.calculateFoodWasteFactors(); }
    private _calories: number;
    get calories(): number {return this._calories; }
    set calories(value: number) { this.setTotalCalories(value); }
    get calculatedCalories(): number { return this.calculateCalories(); }
    get allQuestionsAnwered(): boolean { return this.checkIfAllQuestionsAnswered(); }

    // Formulated questions and answers
    public readonly questionObjects: Question[] = [
      {
        questionConstant: Questions.general_nutrition,
        text: 'Wie ernährst Du dich vorwiegend?',
        options: [
          'Vegan',
          'Vegetarisch',
          'Mischkost'
        ],
        values: [
          GeneralNutritionAnswers.vegan,
          GeneralNutritionAnswers.vegetarian,
          GeneralNutritionAnswers.mixed
        ]
      },
      {
        questionConstant: Questions.meat,
        text: 'Wie häufig isst Du Fleisch, Wurst oder andere Produkte die Fleisch enthalten?',
        options: [
          'Nie',
          'Weniger als der Durchschnitt',
          'Durchschnitt',
          'Häufiger als der Durchschnitt',
          'Viel häufiger als der Durchschnitt'
        ],
        values: [
          StandardAnswers.never,
          StandardAnswers.rarely,
          StandardAnswers.nowAndThen,
          StandardAnswers.often,
          StandardAnswers.frequently
        ]
      },
      {
        questionConstant: Questions.fish,
        text: 'Wie häufig isst Du Fisch oder Meeresfrüchte?',
        options: [
          'Nie',
          'Weniger als der Durchschnitt',
          'Durchschnitt',
          'Häufiger als der Durchschnitt',
          'Viel häufiger als der Durchschnitt'
        ],
        values: [
          StandardAnswers.never,
          StandardAnswers.rarely,
          StandardAnswers.nowAndThen,
          StandardAnswers.often,
          StandardAnswers.frequently
        ]
      },
      {
        questionConstant: Questions.eggs,
        text: 'Wie häufig isst Du Eier oder Produkte die Ei enthalten?',
        options: [
          'Nie',
          'Weniger als der Durchschnitt',
          'Durchschnitt',
          'Häufiger als der Durchschnitt',
          'Viel häufiger als der Durchschnitt'
        ],
        values: [
          StandardAnswers.never,
          StandardAnswers.rarely,
          StandardAnswers.nowAndThen,
          StandardAnswers.often,
          StandardAnswers.frequently
        ]
      },
      {
        questionConstant: Questions.cheese,
        text: 'Wie häufig isst Du Käse?',
        options: [
          'Nie',
          'Weniger als der Durchschnitt',
          'Durchschnitt',
          'Häufiger als der Durchschnitt',
          'Viel häufiger als der Durchschnitt'
        ],
        values: [
          StandardAnswers.never,
          StandardAnswers.rarely,
          StandardAnswers.nowAndThen,
          StandardAnswers.often,
          StandardAnswers.frequently
        ]
      },
      {
        questionConstant: Questions.dairy,
        text: 'Wie häufig isst Du andere Milchprodukte?',
        options: [
          'Nie',
          'Weniger als der Durchschnitt',
          'Durchschnitt',
          'Häufiger als der Durchschnitt',
          'Viel häufiger als der Durchschnitt'
        ],
        values: [
          StandardAnswers.never,
          StandardAnswers.rarely,
          StandardAnswers.nowAndThen,
          StandardAnswers.often,
          StandardAnswers.frequently
        ]
      },
      {
        questionConstant: Questions.nuts,
        text: 'Wie häufig isst Du Nüsse, Kerne oder Samen?',
        options: [
          'Nie',
          'Weniger als der Durchschnitt',
          'Durchschnitt',
          'Häufiger als der Durchschnitt',
          'Viel häufiger als der Durchschnitt'
        ],
        values: [
          StandardAnswers.never,
          StandardAnswers.rarely,
          StandardAnswers.nowAndThen,
          StandardAnswers.often,
          StandardAnswers.frequently
        ]
      },
      {
        questionConstant: Questions.frozen_food,
        text: 'Wie häufig isst Du Tiefkühlkost?',
        options: [
          'Nie',
          'Weniger als der Durchschnitt',
          'Durchschnitt',
          'Häufiger als der Durchschnitt',
          'Viel häufiger als der Durchschnitt'
        ],
        values: [
          StandardAnswers.never,
          StandardAnswers.rarely,
          StandardAnswers.nowAndThen,
          StandardAnswers.often,
          StandardAnswers.frequently
        ]
      },
      {
        questionConstant: Questions.sweets,
        text: 'Wie häufig isst Du Süßigkeiten?',
        options: [
          'Nie',
          'Weniger als der Durchschnitt',
          'Durchschnitt',
          'Häufiger als der Durchschnitt',
          'Viel häufiger als der Durchschnitt'
        ],
        values: [
          StandardAnswers.never,
          StandardAnswers.rarely,
          StandardAnswers.nowAndThen,
          StandardAnswers.often,
          StandardAnswers.frequently
        ]
      },
      {
        questionConstant: Questions.food_waste,
        text: 'Wie häufig wandern bei Dir Lebensmittel in den Müll?',
        options: [
          'Weniger als der Durchschnitt',
          'Durchschnitt',
          'Häufiger als der Durchschnitt'
        ],
        values: [
          FoodWasteAswers.rarely,
          FoodWasteAswers.nowAndThen,
          FoodWasteAswers.often
        ]
      }
    ];

    // nutrition types
    meat: FoodType;
    pork: FoodType;
    beef: FoodType;
    chicken: FoodType;
    fish: FoodType;
    egg: FoodType;
    cheese: FoodType;
    dairy: FoodType;
    milk: FoodType;
    joghurt: FoodType;
    cream: FoodType;
    butter: FoodType;
    fruit: FoodType;
    vegetables: FoodType;
    grain: FoodType;
    nuts: FoodType;
    frozenProducts: FoodType;
    oil: FoodType;
    sweets: FoodType;

    // calculated results
    get totalEmissionsPerDay(): number { return this.meat.emissions + this.fish.emissions + this.egg.emissions +
        this.cheese.emissions + this.dairy.emissions + this.fruit.emissions + this.vegetables.emissions +
        this.grain.emissions + this.frozenProducts.emissions + this.nuts.emissions + this.oil.emissions + this.sweets.emissions; }
    get totalEmissionsPerYear(): number { return this.totalEmissionsPerDay * 365.25 / 1000; }
    get totalVeganEmissionsPerDay(): number { return this.fruit.emissions + this.vegetables.emissions +
        this.grain.emissions + this.nuts.emissions; }
    get totalVeganEmissionsPerYear(): number { return this.totalVeganEmissionsPerDay * 365.25 / 1000; }
    get totalMeatEmissionsPerDay(): number { return this.meat.emissions + this.fish.emissions; }
    get totalMeatEmissionsPerYear(): number { return this.totalMeatEmissionsPerDay * 365.25 / 1000; }
    get totalLactoOvoEmissionsPerDay(): number { return this.egg.emissions + this.cheese.emissions + this.dairy.emissions; }
    get totalLactoOvoEmissionsPerYear(): number { return this.totalLactoOvoEmissionsPerDay * 365.25 / 1000; }
    get totalOtherEmissionsPerDay(): number { return this.frozenProducts.emissions + this.oil.emissions + this.sweets.emissions; }
    get totalOtherEmissionsPerYear(): number { return this.totalOtherEmissionsPerDay * 365.25 / 1000; }
    get totalEmissionsPerDayWithoutWaste(): number { return this.meat.emissionsWithoutWaste + this.fish.emissionsWithoutWaste
        + this.egg.emissionsWithoutWaste + this.cheese.emissionsWithoutWaste + this.dairy.emissionsWithoutWaste
        + this.fruit.emissionsWithoutWaste + this.vegetables.emissionsWithoutWaste + this.grain.emissionsWithoutWaste
        + this.frozenProducts.emissionsWithoutWaste + this.nuts.emissionsWithoutWaste + this.oil.emissionsWithoutWaste
        + this.sweets.emissionsWithoutWaste; }
    get totalEmissionsPerYearWithoutWaste(): number { return this.totalEmissionsPerDayWithoutWaste * 365.25 / 1000; }
    get totalFoodAmount(): number { return this.meat.amount + this.fish.amount + this.egg.amount + this.cheese.amount
        + this.dairy.amount + this.fruit.amount + this.grain.amount + this.vegetables.amount + this.frozenProducts.amount
        + this.nuts.amount + this.oil.amount + this.sweets.amount; }
    get totalFoodKgs(): number { return this.totalFoodAmount / 1000; }
    get averageEmissionsPerDay(): number {
        const q = new Questionnaire();
        q.setDefaultAnswers();
        q.calories = this.calories;
        return q.totalEmissionsPerDay;
    }
    get averageEmissionsPerYear(): number {
        const q = new Questionnaire(); q.setDefaultAnswers(); q.calories = this.calories; return q.totalEmissionsPerYear;
    }
    get averageVeganEmissionsPerDay(): number { const q = new Questionnaire(); q.setDefaultAnswers(); q.calories = this.calories; q.generalNutritionType = GeneralNutritionAnswers.vegan; return q.totalEmissionsPerDay; }
    get averageVeganEmissionsPerYear(): number { const q = new Questionnaire(); q.setDefaultAnswers(); q.calories = this.calories; q.generalNutritionType = GeneralNutritionAnswers.vegan; return q.totalEmissionsPerYear; }
    get meets1_5degree67percent(): boolean { return numbers.ww_daily_budget_1_5_degrees_67_chance_per_calorie * this.calories >= this.totalEmissionsPerDay; }
    get meets2degree67percent(): boolean { return numbers.ww_daily_budget_2_degrees_67_chance_per_calorie * this.calories >= this.totalEmissionsPerDay; }
    get meets1_5degree50percent(): boolean { return numbers.ww_daily_budget_1_5_degrees_50_chance_per_calorie * this.calories >= this.totalEmissionsPerDay; }
    get meets2degree50percent(): boolean { return numbers.ww_daily_budget_2_degrees_50_chance_per_calorie * this.calories >= this.totalEmissionsPerDay; }
    get meets1_5degree33percent(): boolean { return numbers.ww_daily_budget_1_5_degrees_33_chance_per_calorie * this.calories >= this.totalEmissionsPerDay; }
    get meets2degree33percent(): boolean { return numbers.ww_daily_budget_2_degrees_33_chance_per_calorie * this.calories >= this.totalEmissionsPerDay; }

    get categoryStatisticsEmissions(): CategoryStatistics {
        const stats = new CategoryStatistics();
        stats.addMany([
            this.meat.emissionsCategoryStatistic,
            this.fish.emissionsCategoryStatistic,
            this.egg.emissionsCategoryStatistic,
            this.cheese.emissionsCategoryStatistic,
            this.dairy.emissionsCategoryStatistic,
            this.vegetables.emissionsCategoryStatistic,
            this.fruit.emissionsCategoryStatistic,
            this.grain.emissionsCategoryStatistic,
            this.nuts.emissionsCategoryStatistic,
            this.frozenProducts.emissionsCategoryStatistic,
            this.oil.emissionsCategoryStatistic,
            this.sweets.emissionsCategoryStatistic
        ]);
        return stats;
    }
    get categoryStatisticsCalories(): CategoryStatistics {
        const stats = new CategoryStatistics();
        stats.addMany([
            this.meat.caloriesCategoryStatistic,
            this.fish.caloriesCategoryStatistic,
            this.egg.caloriesCategoryStatistic,
            this.cheese.caloriesCategoryStatistic,
            this.dairy.caloriesCategoryStatistic,
            this.vegetables.caloriesCategoryStatistic,
            this.fruit.caloriesCategoryStatistic,
            this.grain.caloriesCategoryStatistic,
            this.nuts.caloriesCategoryStatistic,
            this.frozenProducts.caloriesCategoryStatistic,
            this.oil.caloriesCategoryStatistic,
            this.sweets.caloriesCategoryStatistic
        ]);
        return stats;
    }

    // change tracking
    public changedSubject: Subject<QuestionnaireChangeEvent> = new Subject<QuestionnaireChangeEvent>();

    constructor(i?: IQuestionnaire) {
      this.pork = new FoodType('Schwein', 'Pork', numbers.ger_avg_pork_perc, numbers.ger_CO2_per_cal_pork, numbers.fp_pork, numbers.cal_pork, numbers.gram_per_portion_pork, numbers.ger_waste_perc_meat);
      this.beef = new FoodType('Rind', 'Beef', numbers.ger_avg_beef_perc, numbers.ger_CO2_per_cal_beef, numbers.fp_beef, numbers.cal_beef, numbers.gram_per_portion_beef, numbers.ger_waste_perc_meat);
      this.chicken = new FoodType('Geflügel', 'Chicken', numbers.ger_avg_chicken_perc, numbers.ger_CO2_per_cal_chicken, numbers.fp_chicken, numbers.cal_chicken, numbers.gram_per_portion_chicken, numbers.ger_waste_perc_meat);
      this.meat = new FoodType('Fleisch', 'Meat', numbers.ger_avg_meat_perc, numbers.ger_CO2_per_cal_meat, numbers.fp_meat, numbers.cal_meat, numbers.gram_per_portion_meat, numbers.ger_waste_perc_meat, [this.pork, this.beef, this.chicken]);
      this.fish = new FoodType('Fisch', 'Fish', numbers.ger_avg_fish_perc, numbers.ger_CO2_per_cal_fish, numbers.fp_fish, numbers.cal_fish, numbers.gram_per_portion_fish, numbers.ger_waste_perc_fish);
      this.egg = new FoodType('Ei', 'Egg', numbers.ger_avg_egg_perc, numbers.ger_CO2_per_cal_egg, numbers.fp_egg, numbers.cal_egg, numbers.gram_per_portion_egg, numbers.ger_waste_perc_egg);
      this.cheese = new FoodType('Käse', 'Cheese', numbers.ger_avg_cheese_perc, numbers.ger_CO2_per_cal_cheese, numbers.fp_cheese, numbers.cal_cheese, numbers.gram_per_portion_cheese, numbers.ger_waste_perc_dairy);
      this.milk = new FoodType('Milch', 'Milk', numbers.ger_avg_milk_perc, numbers.ger_CO2_per_cal_milk, numbers.fp_milk, numbers.cal_milk, numbers.gram_per_portion_milk, numbers.ger_waste_perc_dairy);
      this.joghurt = new FoodType('Joghurt', 'Joghurt', numbers.ger_avg_joghurt_perc, numbers.ger_CO2_per_cal_joghurt, numbers.fp_joghurt, numbers.cal_joghurt, numbers.gram_per_portion_joghurt, numbers.ger_waste_perc_dairy);
      this.cream = new FoodType('Sahne', 'Cream', numbers.ger_avg_cream_perc, numbers.ger_CO2_per_cal_cream, numbers.fp_cream, numbers.cal_cream, numbers.gram_per_portion_cream, numbers.ger_waste_perc_dairy);
      this.butter = new FoodType('Butter', 'Butter', numbers.ger_avg_butter_perc, numbers.ger_CO2_per_cal_butter, numbers.fp_butter, numbers.cal_butter, numbers.gram_per_portion_butter, numbers.ger_waste_perc_dairy);
      this.dairy = new FoodType('Milchprodukte', 'Dairy', numbers.ger_avg_dairy_perc, numbers.ger_CO2_per_cal_dairy, numbers.fp_dairy, numbers.cal_dairy, numbers.gram_per_portion_dairy, numbers.ger_waste_perc_dairy, [this.milk, this.joghurt, this.cream, this.butter]);
      this.fruit = new FoodType('Obst', 'Fruits', numbers.ger_avg_fruit_perc, numbers.ger_CO2_per_cal_fruit, numbers.fp_fruit, numbers.cal_fruit, numbers.gram_per_portion_fruit, numbers.ger_waste_perc_fruit);
      this.vegetables = new FoodType('Gemüse', 'Vegetables', numbers.ger_avg_vegetables_perc, numbers.ger_CO2_per_cal_vegetables, numbers.fp_vegetables, numbers.cal_vegetables, numbers.gram_per_portion_vegetables, numbers.ger_waste_perc_vegetables);
      this.grain = new FoodType('Getreide', 'Grain', numbers.ger_avg_grain_perc, numbers.ger_CO2_per_cal_grain, numbers.fp_grain, numbers.cal_grain, numbers.gram_per_portion_grain, numbers.ger_waste_perc_grain);
      this.nuts = new FoodType('Nüsse und Kerne', 'Nuts', numbers.ger_avg_nuts_perc, numbers.ger_CO2_per_cal_nuts, numbers.fp_nuts, numbers.cal_nuts, numbers.gram_per_portion_nuts, numbers.ger_waste_perc_nuts);
      this.frozenProducts = new FoodType('Tiefkühlprodukte', 'Frozen', numbers.ger_avg_frozen_perc, numbers.ger_CO2_per_cal_frozen, numbers.fp_frozen, numbers.cal_frozen, numbers.gram_per_portion_frozen, numbers.ger_waste_perc_frozen);
      this.oil = new FoodType('Öle', 'Oil', numbers.ger_avg_oil_perc, numbers.ger_CO2_per_cal_oil, numbers.fp_oil, numbers.cal_oil, numbers.gram_per_portion_oil, numbers.ger_waste_perc_oil);
      this.sweets = new FoodType('Süßigkeiten', 'Sweets', numbers.ger_avg_sweets_perc, numbers.ger_CO2_per_cal_sweets, numbers.fp_sweets, numbers.cal_sweets, numbers.gram_per_portion_sweets, numbers.ger_waste_perc_sweets);

      if (i) {
        this.currentQuestion = i.currentQuestion;
        this.gender = i.gender;
        this.size = i.size;
        this.weight = i.weight;
        this.age = i.age;
        this.activityLevel = i.activityLevel;
        this.generalNutritionType = i.generalNutritionType;
        this.eggsAnswer = i.eggsAnswer;
        this.cheeseAnswer = i.cheeseAnswer;
        this.dairyAnswer = i.dairyAnswer;
        this.meatAnswer = i.meatAnswer;
        this.fishAnswer = i.fishAnswer;
        this.nutsAnswer = i.nutsAnswer;
        this.frozenFoodAnswer = i.frozenFoodAnswer;
        this.sweetsAnswer = i.sweetsAnswer;
        this.foodWasteAnswer = i.foodWasteAnswer;
        this.calories = i.calories;
      }
    }

    // method that sets the default answers for all questions
    public setDefaultAnswers() {
      this.generalNutritionType = GeneralNutritionAnswers.mixed;
      this.meatAnswer = StandardAnswers.nowAndThen;
      this.fishAnswer = StandardAnswers.nowAndThen;
      this.eggsAnswer = StandardAnswers.nowAndThen;
      this.cheeseAnswer = StandardAnswers.nowAndThen;
      this.dairyAnswer = StandardAnswers.nowAndThen;
      this.nutsAnswer = StandardAnswers.nowAndThen;
      this.frozenFoodAnswer = StandardAnswers.nowAndThen;
      this.sweetsAnswer = StandardAnswers.nowAndThen;
      this.foodWasteAnswer = FoodWasteAswers.nowAndThen;
    }

    public goToFirstQuestion(): void {
      this.currentQuestion = this.questionOrder.firstQuestion;
    }

    // method that sets all answers given by another questionnaire
    public setAnswersFromQuestionnaire(questionnaire: Questionnaire): void {
      this.generalNutritionType = questionnaire.generalNutritionType;
      this.meatAnswer = questionnaire.meatAnswer;
      this.fishAnswer = questionnaire.fishAnswer;
      this.eggsAnswer = questionnaire.eggsAnswer;
      this.cheeseAnswer = questionnaire.cheeseAnswer;
      this.dairyAnswer = questionnaire.dairyAnswer;
      this.nutsAnswer = questionnaire.nutsAnswer;
      this.frozenFoodAnswer = questionnaire.frozenFoodAnswer;
      this.sweetsAnswer = questionnaire.sweetsAnswer;
      this.foodWasteAnswer = questionnaire.foodWasteAnswer;
      this.changedSubject.next({type: QuestionnaireChangeType.miscellaneous});
    }

    // method that calculates the results of the questionnaire
    // Should not be needed anymore, as results are updated automatically after an answer was changed
    public calculateResults() {
      this.calculateNutritionPercentages();
      this.calculateFoodWasteFactors();
      this.setTotalCalories(this.calories);
    }

    public setAnswerForQuestion(question: Questions, answer: number): void {
      switch (question) {
        case(Questions.general_nutrition): {
          this.generalNutritionType = answer;
          return;
        }
        case(Questions.meat): {
          this.meatAnswer = answer;
          return;
        }
        case(Questions.fish): {
          this.fishAnswer = answer;
          return;
        }
        case(Questions.eggs): {
          this.eggsAnswer = answer;
          return;
        }
        case(Questions.cheese): {
          this.cheeseAnswer = answer;
          return;
        }
        case(Questions.dairy): {
          this.dairyAnswer = answer;
          return;
        }
        case(Questions.nuts): {
          this.nutsAnswer = answer;
          return;
        }
        case(Questions.frozen_food): {
          this.frozenFoodAnswer = answer;
          return;
        }
        case(Questions.sweets): {
          this.sweetsAnswer = answer;
          return;
        }
        case(Questions.food_waste): {
          this.foodWasteAnswer = answer;
          return;
        }
      }
    }

    // method which calculates the answer option helpers
    public calculateAnswerOption(question: number, answer: number, round: boolean = false): number {
      const q = new Questionnaire();
      q.calories = this.calories;
      q.generalNutritionType = this.generalNutritionType;
      q.meatAnswer = (this.meatAnswer !== undefined) ? this.meatAnswer : StandardAnswers.nowAndThen;
      q.fishAnswer = (this.fishAnswer !== undefined) ? this.fishAnswer : StandardAnswers.nowAndThen;
      q.eggsAnswer = (this.eggsAnswer !== undefined) ? this.eggsAnswer : StandardAnswers.nowAndThen;
      q.cheeseAnswer = (this.cheeseAnswer !== undefined) ? this.cheeseAnswer : StandardAnswers.nowAndThen;
      q.dairyAnswer = (this.dairyAnswer !== undefined) ? this.dairyAnswer : StandardAnswers.nowAndThen;
      q.nutsAnswer = (this.nutsAnswer !== undefined) ? this.nutsAnswer : StandardAnswers.nowAndThen;
      q.frozenFoodAnswer = (this.frozenFoodAnswer !== undefined) ? this.frozenFoodAnswer : StandardAnswers.nowAndThen;
      q.sweetsAnswer = (this.sweetsAnswer !== undefined) ? this.sweetsAnswer : StandardAnswers.nowAndThen;
      q.foodWasteAnswer = (this.foodWasteAnswer !== undefined) ? this.foodWasteAnswer : FoodWasteAswers.nowAndThen;
      switch (question) {
        case(Questions.meat): {
          q.meatAnswer = answer;
          return round ? this.roundFunction(q.meat.amountWithoutWaste) : q.meat.amountWithoutWaste;
        }
        case(Questions.fish): {
          q.fishAnswer = answer;
          return round ? this.roundFunction(q.fish.amountWithoutWaste) : q.fish.amountWithoutWaste;
        }
        case(Questions.eggs): {
          q.eggsAnswer = answer;
          return round ? this.roundFunction(q.egg.amountWithoutWaste) : q.egg.amountWithoutWaste;
        }
        case(Questions.cheese): {
          q.cheeseAnswer = answer;
          return round ? this.roundFunction(q.cheese.amountWithoutWaste) : q.cheese.amountWithoutWaste;
        }
        case(Questions.dairy): {
          q.dairyAnswer = answer;
          return round ? this.roundFunction(q.dairy.amountWithoutWaste) : q.dairy.amountWithoutWaste;
        }
        case(Questions.nuts): {
          q.nutsAnswer = answer;
          return round ? this.roundFunction(q.nuts.amountWithoutWaste) : q.nuts.amountWithoutWaste;
        }
        case(Questions.frozen_food): {
          q.frozenFoodAnswer = answer;
          return round ? this.roundFunction(q.frozenProducts.amountWithoutWaste) : q.frozenProducts.amountWithoutWaste;
        }
        case(Questions.sweets): {
          q.sweetsAnswer = answer;
          return round ? this.roundFunction(q.sweets.amountWithoutWaste) : q.sweets.amountWithoutWaste;
        }
        case(Questions.food_waste): {
          q.foodWasteAnswer = answer;
          return 1 - q.totalEmissionsPerDayWithoutWaste / q.totalEmissionsPerDay;
        }
        default: {
          return -1;
        }
      }
    }

    // method which calculates the answer option helpers
    public calculateAnswerOptionPortions(question: number, answer: number): Portion {
      const q = new Questionnaire();
      q.calories = this.calories;
      q.generalNutritionType = this.generalNutritionType;
      q.meatAnswer = (this.meatAnswer !== undefined) ? this.meatAnswer : StandardAnswers.nowAndThen;
      q.fishAnswer = (this.fishAnswer !== undefined) ? this.fishAnswer : StandardAnswers.nowAndThen;
      q.eggsAnswer = (this.eggsAnswer !== undefined) ? this.eggsAnswer : StandardAnswers.nowAndThen;
      q.cheeseAnswer = (this.cheeseAnswer !== undefined) ? this.cheeseAnswer : StandardAnswers.nowAndThen;
      q.dairyAnswer = (this.dairyAnswer !== undefined) ? this.dairyAnswer : StandardAnswers.nowAndThen;
      q.nutsAnswer = (this.nutsAnswer !== undefined) ? this.nutsAnswer : StandardAnswers.nowAndThen;
      q.frozenFoodAnswer = (this.frozenFoodAnswer !== undefined) ? this.frozenFoodAnswer : StandardAnswers.nowAndThen;
      q.sweetsAnswer = (this.sweetsAnswer !== undefined) ? this.sweetsAnswer : StandardAnswers.nowAndThen;
      q.foodWasteAnswer = (this.foodWasteAnswer !== undefined) ? this.foodWasteAnswer : FoodWasteAswers.nowAndThen;
      switch (question) {
        case(Questions.meat): {
          q.meatAnswer = answer;
          return this.portionCalculator(q.meat.portionsPerDay, false);
        }
        case(Questions.fish): {
          q.fishAnswer = answer;
          return this.portionCalculator(q.fish.portionsPerDay, false);
        }
        case(Questions.eggs): {
          q.eggsAnswer = answer;
          return this.portionCalculator(q.egg.portionsPerDay, false);
        }
        case(Questions.cheese): {
          q.cheeseAnswer = answer;
          return this.portionCalculator(q.cheese.portionsPerDay, false);
        }
        case(Questions.dairy): {
          q.dairyAnswer = answer;
          return this.portionCalculator(q.dairy.portionsPerDay, false);
        }
        case(Questions.nuts): {
          q.nutsAnswer = answer;
          return this.portionCalculator(q.nuts.portionsPerDay, false);
        }
        case(Questions.frozen_food): {
          q.frozenFoodAnswer = answer;
          return this.portionCalculator(q.frozenProducts.portionsPerDay, false);
        }
        case(Questions.sweets): {
          q.sweetsAnswer = answer;
          return this.portionCalculator(q.sweets.portionsPerDay, false);
        }
        default: {
          return this.portionCalculator(0);
        }
      }
    }

    // method which calculates the answer option helpers for all answers at once
    public calculateAllAnswerOptionPortions(question: number): Portion[] {
      const q = new Questionnaire();
      q.calories = this.calories;
      q.generalNutritionType = this.generalNutritionType;
      q.meatAnswer = (this.meatAnswer !== undefined) ? this.meatAnswer : StandardAnswers.nowAndThen;
      q.fishAnswer = (this.fishAnswer !== undefined) ? this.fishAnswer : StandardAnswers.nowAndThen;
      q.eggsAnswer = (this.eggsAnswer !== undefined) ? this.eggsAnswer : StandardAnswers.nowAndThen;
      q.cheeseAnswer = (this.cheeseAnswer !== undefined) ? this.cheeseAnswer : StandardAnswers.nowAndThen;
      q.dairyAnswer = (this.dairyAnswer !== undefined) ? this.dairyAnswer : StandardAnswers.nowAndThen;
      q.nutsAnswer = (this.nutsAnswer !== undefined) ? this.nutsAnswer : StandardAnswers.nowAndThen;
      q.frozenFoodAnswer = (this.frozenFoodAnswer !== undefined) ? this.frozenFoodAnswer : StandardAnswers.nowAndThen;
      q.sweetsAnswer = (this.sweetsAnswer !== undefined) ? this.sweetsAnswer : StandardAnswers.nowAndThen;
      q.foodWasteAnswer = (this.foodWasteAnswer !== undefined) ? this.foodWasteAnswer : FoodWasteAswers.nowAndThen;
      switch (question) {
        case(Questions.meat): {
          const ret: Portion[] = [];
          for (const answer in StandardAnswers) {
            if (isNaN(+answer) === false) {
              q.meatAnswer = +answer;
              ret.push(this.portionCalculator(q.meat.portionsPerDay, false));
            }
          }
          return ret;
        }
        case(Questions.fish): {
          const ret: Portion[] = [];
          for (const answer in StandardAnswers) {
            if (isNaN(+answer) === false) {
              q.fishAnswer = +answer;
              ret.push(this.portionCalculator(q.fish.portionsPerDay, false));
            }
          }
          return ret;
        }
        case(Questions.eggs): {
          const ret: Portion[] = [];
          for (const answer in StandardAnswers) {
            if (isNaN(+answer) === false) {
              q.eggsAnswer = +answer;
              ret.push(this.portionCalculator(q.egg.portionsPerDay, false));
            }
          }
          return ret;
        }
        case(Questions.cheese): {
          const ret: Portion[] = [];
          for (const answer in StandardAnswers) {
            if (isNaN(+answer) === false) {
              q.cheeseAnswer = +answer;
              ret.push(this.portionCalculator(q.cheese.portionsPerDay, false));
            }
          }
          return ret;
        }
        case(Questions.dairy): {
          const ret: Portion[] = [];
          for (const answer in StandardAnswers) {
            if (isNaN(+answer) === false) {
              q.dairyAnswer = +answer;
              ret.push(this.portionCalculator(q.dairy.portionsPerDay, false));
            }
          }
          return ret;
        }
        case(Questions.nuts): {
          const ret: Portion[] = [];
          for (const answer in StandardAnswers) {
            if (isNaN(+answer) === false) {
              q.nutsAnswer = +answer;
              ret.push(this.portionCalculator(q.nuts.portionsPerDay, false));
            }
          }
          return ret;
        }
        case(Questions.frozen_food): {
          const ret: Portion[] = [];
          for (const answer in StandardAnswers) {
            if (isNaN(+answer) === false) {
              q.frozenFoodAnswer = +answer;
              ret.push(this.portionCalculator(q.frozenProducts.portionsPerDay, false));
            }
          }
          return ret;
        }
        case(Questions.sweets): {
          const ret: Portion[] = [];
          for (const answer in StandardAnswers) {
            if (isNaN(+answer) === false) {
              q.sweetsAnswer = +answer;
              ret.push(this.portionCalculator(q.sweets.portionsPerDay, false));
            }
          }
          return ret;
        }
        default: {
          return [];
        }
      }
    }

    // method that rounds answer values to rough values
    private roundFunction(val: number): number {
      // round rules:
      // val <  5: whole number
      // val < 30: multiples of 5
      // val <100: multiples of 10
      // val <200: multiples of 20
      // val <500: multiples of 50
      // val >=500: multiples of 100
      const ret: number = Math.round(val);
      if (val < 5){
        return ret;
      }else if (val < 30) {
        return this.roundToClosestMultipleOf(ret, 5);
      }else if (val < 100){
        return Math.round(ret / 10) * 10;
      }else if (val < 200){
        return this.roundToClosestMultipleOf(ret, 20);
      }else if (val < 500){
        return this.roundToClosestMultipleOf(ret, 50);
      }else{
        return Math.round(ret / 100) * 100;
      }
    }

    // helper function to round to closest multipe of a certain number
    private roundToClosestMultipleOf(val: number, multiple: number): number{
      const modulo = val % multiple;
      return modulo >= multiple / 2 ? Math.ceil(val / multiple) * multiple : Math.floor(val / multiple) * multiple;
    }

    // helper function to calculate an appropriate Portion out of a value
    private portionCalculator(val: number, round: boolean = true): Portion {
      const perWeek: number = val * 7;
      if (perWeek >= 5.5){
        return {amount: round ? Math.round(val) : val, per: PortionUnits.DAY};
      }else{
        return {amount: round ? Math.round(perWeek) : perWeek, per: PortionUnits.WEEK};
      }
    }

    // helper function which returns the portion string to display
    public portionHelper(question: number, answer: number): string {
      const res: Portion = this.calculateAnswerOptionPortions(question, answer);
      const numberString: string = this.numberToWordText(res.amount);
      const unitString: string = res.per === PortionUnits.DAY ? 'täglich' : 'pro Woche';
      const orMore: string = (Object.keys(StandardAnswers).length / 2) - 1 === answer ? ' oder öfter' : '';
      return numberString + unitString + orMore;
    }

    // Helper function which converts the portion number to text.
    // If more precision is required, it also returns 'from...to' texts.
    private numberToWordText(val: number, morePrecision: boolean = false): string {
      if (morePrecision){
        const decimals: number = val - Math.floor(val);
        if (decimals >= 0.25 && decimals < 0.75 && val > 1) {
          return this.numberToWordPart(Math.floor(val)) + ' bis ' + this.numberToWord(Math.ceil(val)) + ' ';
        }else{
          return this.numberToWord(Math.round(val)) + ' ';
        }
      }else{
        return this.numberToWord(Math.round(val)) + ' ';
      }
    }

    private numberToWord(val: number): string {
      switch (val){
        case 1:
          return 'einmal';
        case 2:
          return 'zweimal';
        case 3:
          return 'dreimal';
        case 4:
          return 'viermal';
        case 5:
          return 'fünfmal';
        case 6:
          return 'sechsmal';
        case 7:
          return 'siebenmal';
        case 8:
          return 'achtmal';
        case 9:
          return 'neunmal';
        default:
          return '';
      }
    }

    private numberToWordPart(val: number): string {
      switch (val){
        case 1:
          return 'ein-';
        case 2:
          return 'zwei-';
        case 3:
          return 'drei-';
        case 4:
          return 'vier-';
        case 5:
          return 'fünf-';
        case 6:
          return 'sechs-';
        case 7:
          return 'sieben-';
        case 8:
          return 'acht-';
        case 9:
          return 'neun-';
        default:
          return '';
      }
    }

    // method which indicates if an emission target can be reached
    public checkEmissionTarget(value: number, target: EmissionTarget): TargetProbability {
      switch (target){
        case EmissionTarget.ONE_POINT_FIVE: {
          if (value <= numbers.ww_yearly_budget_1_5_degrees_67_chance){
            return TargetProbability.MORE_THAN_67;
          }else if (value <= numbers.ww_yearly_budget_1_5_degrees_50_chance){
            return TargetProbability.MORE_THAN_50;
          }else if (value <= numbers.ww_yearly_budget_1_5_degrees_33_chance){
            return TargetProbability.MORE_THAN_33;
          }else {
            return TargetProbability.UNLIKELY;
          }
        }
        case EmissionTarget.TWO: {
          if (value <= numbers.ww_yearly_budget_2_degrees_67_chance){
            return TargetProbability.MORE_THAN_67;
          }else if (value <= numbers.ww_yearly_budget_2_degrees_50_chance){
            return TargetProbability.MORE_THAN_50;
          }else if (value <= numbers.ww_yearly_budget_2_degrees_33_chance){
            return TargetProbability.MORE_THAN_33;
          }else {
            return TargetProbability.UNLIKELY;
          }
        }
        default: {
          return TargetProbability.UNLIKELY;
        }
      }
    }

    // method that calculates nutrition percentages based on the answers given
    private calculateNutritionPercentages() {
      let eggsTemp = 0;
      let cheeseTemp = 0;
      let dairyTemp = 0;
      let meatTemp = 0;
      let fishTemp = 0;

      if ( this.generalNutritionType === GeneralNutritionAnswers.vegetarian ) {
        eggsTemp = this.fallbackToStandardAnswer(this.eggsAnswer) * numbers.ger_avg_egg_perc;
        cheeseTemp = this.fallbackToStandardAnswer(this.cheeseAnswer) * numbers.ger_avg_cheese_perc;
        dairyTemp = this.fallbackToStandardAnswer(this.dairyAnswer) * numbers.ger_avg_dairy_perc;
        meatTemp = 0;
        fishTemp = 0;
      }
      if ( this.generalNutritionType === GeneralNutritionAnswers.mixed ) {
        eggsTemp = this.fallbackToStandardAnswer(this.eggsAnswer) * numbers.ger_avg_egg_perc;
        cheeseTemp = this.fallbackToStandardAnswer(this.cheeseAnswer) * numbers.ger_avg_cheese_perc;
        dairyTemp = this.fallbackToStandardAnswer(this.dairyAnswer) * numbers.ger_avg_dairy_perc;
        meatTemp = this.fallbackToStandardAnswer(this.meatAnswer) * numbers.ger_avg_meat_perc;
        fishTemp = this.fallbackToStandardAnswer(this.fishAnswer) * numbers.ger_avg_fish_perc;
      }
      const nutsTemp: number = this.fallbackToStandardAnswer(this.nutsAnswer) * numbers.ger_avg_nuts_perc;
      const fruitTemp: number = this.fallbackToStandardAnswer(this.fruitsAnswer) * numbers.ger_avg_fruit_perc;
      const vegetablesTemp: number = this.fallbackToStandardAnswer(this.vegetablesAnswer) * numbers.ger_avg_vegetables_perc;
      const grainTemp: number = this.fallbackToStandardAnswer(this.grainAnswer) * numbers.ger_avg_grain_perc;

      this.oil.nutritionPercentage = numbers.ger_avg_oil_perc;

      let sumTemp: number;
      let fixPercentages: number;
      switch (this.type){
        // Fast tracker detailed calculation (sweets and frozen products can have 100% etc.)
        case QuestionnaireType.tracker_details: {
          const frozenTemp: number = this.fallbackToStandardAnswer(this.frozenFoodAnswer) * numbers.ger_avg_nuts_perc;
          const sweetsTemp: number = this.fallbackToStandardAnswer(this.sweetsAnswer) * numbers.ger_avg_nuts_perc;
          sumTemp = eggsTemp + cheeseTemp + dairyTemp + meatTemp + fishTemp + fruitTemp + vegetablesTemp
              + grainTemp + nutsTemp + frozenTemp + sweetsTemp;
          fixPercentages = 1 - (this.oil.nutritionPercentage);
          this.frozenProducts.nutritionPercentage = frozenTemp / sumTemp * fixPercentages;
          this.sweets.nutritionPercentage = sweetsTemp / sumTemp * fixPercentages;
          break;
        }
        // Standard calculation (sweets and frozen products are only weighted by a relative factor)
        default: {
          sumTemp = eggsTemp + cheeseTemp + dairyTemp + meatTemp + fishTemp + fruitTemp + vegetablesTemp
              + grainTemp + nutsTemp;
          this.frozenProducts.nutritionPercentage = numbers.ger_avg_frozen_perc
              * this.relativeFactor(this.fallbackToStandardAnswer(this.frozenFoodAnswer));
          this.sweets.nutritionPercentage = numbers.ger_avg_sweets_perc *
              this.relativeFactor(this.fallbackToStandardAnswer(this.sweetsAnswer));
          fixPercentages = 1 - (this.oil.nutritionPercentage + this.frozenProducts.nutritionPercentage
              + this.sweets.nutritionPercentage);
        }
      }

      this.meat.nutritionPercentage = meatTemp / sumTemp * fixPercentages;
      this.fish.nutritionPercentage = fishTemp / sumTemp * fixPercentages;
      this.egg.nutritionPercentage = eggsTemp / sumTemp * fixPercentages;
      this.cheese.nutritionPercentage = cheeseTemp / sumTemp * fixPercentages;
      this.dairy.nutritionPercentage = dairyTemp / sumTemp * fixPercentages;
      this.fruit.nutritionPercentage = fruitTemp / sumTemp * fixPercentages;
      this.vegetables.nutritionPercentage = vegetablesTemp / sumTemp * fixPercentages;
      this.grain.nutritionPercentage = grainTemp / sumTemp * fixPercentages;
      this.nuts.nutritionPercentage = nutsTemp / sumTemp * fixPercentages;
    }

    private fallbackToStandardAnswer(answer: number): number {
      if (answer === undefined) { return StandardAnswers.nowAndThen; }
      return answer;
    }

    private relativeFactor(answer: number): number {
      switch (answer){
        case StandardAnswers.never: {
          return numbers.relative_never;
        }
        case StandardAnswers.rarely: {
          return numbers.relative_rarely;
        }
        case StandardAnswers.nowAndThen: {
          return numbers.relative_nowAndThen;
        }
        case StandardAnswers.often: {
          return numbers.relative_often;
        }
        case StandardAnswers.frequently: {
          return numbers.relative_frequently;
        }
        default: {
          return -1;
        }
      }
    }

    // Update waste percentages according to answer
    private calculateFoodWasteFactors() {
      let wasteFactor = 0;
      const wasteAnswer = (this.foodWasteAnswer !== undefined) ? this.foodWasteAnswer : FoodWasteAswers.nowAndThen;
      switch (wasteAnswer) {
        case FoodWasteAswers.rarely: {
          wasteFactor = numbers.waste_rarely;
          break;
        }
        case FoodWasteAswers.nowAndThen: {
          wasteFactor = numbers.waste_nowAndThen;
          break;
        }
        case FoodWasteAswers.often: {
          wasteFactor = numbers.waste_often;
          break;
        }
     }
      this.meat.wastePercentage = wasteFactor * numbers.ger_waste_perc_meat;
      this.pork.wastePercentage = wasteFactor * numbers.ger_waste_perc_meat;
      this.beef.wastePercentage = wasteFactor * numbers.ger_waste_perc_meat;
      this.chicken.wastePercentage = wasteFactor * numbers.ger_waste_perc_meat;
      this.fish.wastePercentage = wasteFactor * numbers.ger_waste_perc_fish;
      this.egg.wastePercentage = wasteFactor * numbers.ger_waste_perc_egg;
      this.cheese.wastePercentage = wasteFactor * numbers.ger_waste_perc_dairy;
      this.dairy.wastePercentage = wasteFactor * numbers.ger_waste_perc_dairy;
      this.milk.wastePercentage = wasteFactor * numbers.ger_waste_perc_dairy;
      this.joghurt.wastePercentage = wasteFactor * numbers.ger_waste_perc_dairy;
      this.cream.wastePercentage = wasteFactor * numbers.ger_waste_perc_dairy;
      this.butter.wastePercentage = wasteFactor * numbers.ger_waste_perc_dairy;
      this.fruit.wastePercentage = wasteFactor * numbers.ger_waste_perc_fruit;
      this.vegetables.wastePercentage = wasteFactor * numbers.ger_waste_perc_vegetables;
      this.grain.wastePercentage = wasteFactor * numbers.ger_waste_perc_grain;
      this.nuts.wastePercentage = wasteFactor * numbers.ger_waste_perc_nuts;
      this.frozenProducts.wastePercentage = wasteFactor * numbers.ger_waste_perc_frozen;
      this.oil.wastePercentage = wasteFactor * numbers.ger_waste_perc_oil;
      this.sweets.wastePercentage = wasteFactor * numbers.ger_waste_perc_sweets;
    }

    // method that sets the total calories to each food type
    private setTotalCalories(value: number) {
      this.meat.setTotalCalories(value);
      this.fish.setTotalCalories(value);
      this.egg.setTotalCalories(value);
      this.cheese.setTotalCalories(value);
      this.dairy.setTotalCalories(value);
      this.fruit.setTotalCalories(value);
      this.vegetables.setTotalCalories(value);
      this.grain.setTotalCalories(value);
      this.nuts.setTotalCalories(value);
      this.frozenProducts.setTotalCalories(value);
      this.oil.setTotalCalories(value);
      this.sweets.setTotalCalories(value);

      if (value !== this.calories){
        const previous = this._calories;
        this._calories = (value !== undefined && value !== null) ? value : 0;
        this.changedSubject.next({type: QuestionnaireChangeType.calories, previousValue: previous, newValue: value});
      }
    }

    // method which calculates total calories based on the gender/age/size/weight/activity level
    private calculateCalories(): number {
      let cal = 0;
      switch (this.gender){
        case(Gender.female): {
          cal = 655.1 + 9.6 * this.weight + 1.8 * this.size - 4.7 * this.age;
          break;
        }
        case(Gender.male): {
          cal = 66.47 + 13.7 * this.weight + 5 * this.size - 6.8 * this.age;
          break;
        }
        case(Gender.diverse): {
          cal = (655.1 + 66.47) / 2 + (13.7 + 9.6) / 2 * this.weight + (5 + 1.8) / 2 * this.size - (6.8 + 4.7) / 2 * this.age;
          break;
        }
      }
      switch (this.activityLevel){
        case(ActivityLevel.sittingOnly): {
          cal *= 1.2;
          break;
        }
        case(ActivityLevel.mainlySitting): {
          cal *= 1.45;
          break;
        }
        case(ActivityLevel.partlySitting): {
          cal *= 1.65;
          break;
        }
        case(ActivityLevel.mainlyStanding): {
          cal *= 1.85;
          break;
        }
        case(ActivityLevel.physicallyDemanding): {
          cal *= 2.3;
          break;
        }
      }
      return cal;
    }

    private checkIfAllQuestionsAnswered(): boolean {
      if (this.generalNutritionType === undefined) { return false; }
      switch (this.generalNutritionType) {
        case(GeneralNutritionAnswers.mixed): {
          if (this.meatAnswer === undefined) { return false; }
          if (this.fishAnswer === undefined) { return false; }
          if (this.eggsAnswer === undefined) { return false; }
          if (this.cheeseAnswer === undefined) { return false; }
          if (this.dairyAnswer === undefined) { return false; }
          if (this.nutsAnswer === undefined) { return false; }
          if (this.frozenFoodAnswer === undefined) { return false; }
          if (this.sweetsAnswer === undefined) { return false; }
          if (this.foodWasteAnswer === undefined) { return false; }
          return true;
        }
        case(GeneralNutritionAnswers.vegetarian): {
          if (this.eggsAnswer === undefined) { return false; }
          if (this.cheeseAnswer === undefined) { return false; }
          if (this.dairyAnswer === undefined) { return false; }
          if (this.nutsAnswer === undefined) { return false; }
          if (this.frozenFoodAnswer === undefined) { return false; }
          if (this.sweetsAnswer === undefined) { return false; }
          if (this.foodWasteAnswer === undefined) { return false; }
          return true;
        }
        case(GeneralNutritionAnswers.vegan): {
          if (this.nutsAnswer === undefined) { return false; }
          if (this.frozenFoodAnswer === undefined) { return false; }
          if (this.sweetsAnswer === undefined) { return false; }
          if (this.foodWasteAnswer === undefined) { return false; }
          return true;
        }
        default: {
          return false;
        }
      }
    }

    // method which generates interface of the class
    public toInterface(): IQuestionnaire {
      return { currentQuestion: this.currentQuestion, gender: this.gender, size: this.size, weight: this.weight,
          age: this.age, activityLevel: this.activityLevel, generalNutritionType: this.generalNutritionType,
          cheeseAnswer: this.cheeseAnswer, eggsAnswer: this.eggsAnswer, dairyAnswer: this.dairyAnswer,
          meatAnswer: this.meatAnswer, fishAnswer: this.fishAnswer, nutsAnswer: this.nutsAnswer,
          frozenFoodAnswer: this.frozenFoodAnswer, sweetsAnswer: this.sweetsAnswer, foodWasteAnswer: this.foodWasteAnswer,
          calories: this.calories};
    }
  }

export class FoodType {
    name: string;
    category: string;
    nutritionPercentage: number;
    footprintPerCalorie: number; // gramm CO2 per calorie
    footprintPerKg: number;
    caloriesPerKg: number;
    gramsPerPortion: number;
    wastePercentage: number;
    hasChildFoods = false;
    hasParentFoods = false;
    childFoods: FoodType[];
    parentFood: FoodType;
    totalCalories = 0;
    get amount(): number { return this.getAmount(); } // amount in gramm
    get amountWithoutWaste(): number { return this.getAmountWithoutWaste(); }
    get portionsPerDay(): number { return this.amountWithoutWaste / this.gramsPerPortion; }
    // get portionsPerDay(): number { return this.nutritionPercentage * numbers.average_portions_per_day};
    get childAmountPercentage(): number { return this.getChildAmountPercentage(); }
    get calories(): number {return this.getCalories(); }
    get emissions(): number { return this.getEmissions(); }
    get emissionsPerYear(): number { return this.emissions * 365 / 1000; }
    get emissionsWithoutWaste(): number { return this.getEmissionsWithoutWaste(); }
    get emissionsCategoryStatistic(): CategoryStatistic {
      return  new CategoryStatistic(this.category, this.emissions);
    }
    get caloriesCategoryStatistic(): CategoryStatistic {
        return  new CategoryStatistic(this.category, this.calories);
    }

  constructor(n: string, c: string, nP: number, fpcal: number, fpkg: number, cpkg: number, gpp: number, wP: number, cF?: FoodType[]) {
      this.name = n;
      this.category = c;
      this.nutritionPercentage = nP;
      this.footprintPerCalorie = fpcal;
      this.footprintPerKg = fpkg;
      this.caloriesPerKg = cpkg;
      this.gramsPerPortion = gpp;
      this.wastePercentage = wP;
      if (cF !== undefined) {
        this.hasChildFoods = true;
        this.childFoods = cF;
        cF.forEach(element => {
          element.parentFood = this;
          element.hasParentFoods = true;
        });
      }
  }

  public getAmountPercentage(totalAmount: number): number {
    return (this.amount / 1000) / totalAmount;
  }

  private getChildAmountPercentage(): number {
    if (this.hasParentFoods){
      return this.amount / this.parentFood.amount;
    }else{
      return 0;
    }
  }

  private getCalories(): number {
    if (this.hasParentFoods === false){
      return this.totalCalories * this.nutritionPercentage;
    }else{
      return this.totalCalories * this.nutritionPercentage * this.parentFood.nutritionPercentage;
    }
  }

  private getAmountWithoutWaste(): number {
    if (this.hasChildFoods === false){
      return this.calories / this.caloriesPerKg * 1000;
    }else{
      let temp = 0;
      this.childFoods.forEach(element => {temp += element.amountWithoutWaste; });
      return temp;
    }
  }

  private getEmissionsWithoutWaste(): number {
    if (this.hasChildFoods === false){
      return this.amountWithoutWaste * this.footprintPerKg * numbers.ger_home_factor;
    }else{
      let temp = 0;
      this.childFoods.forEach(element => {temp += element.emissionsWithoutWaste; });
      return temp * numbers.ger_home_factor;
    }
  }

  private getAmount(): number {
    if (this.hasChildFoods === false){
      return this.amountWithoutWaste / (1 - this.wastePercentage);
    }else{
      let temp = 0;
      this.childFoods.forEach(element => {temp += element.amount; });
      return temp;
    }
  }

  private getEmissions(): number {
    if (this.hasChildFoods === false){
      return this.amount * this.footprintPerKg * numbers.ger_home_factor;
    }else{
      let temp = 0;
      this.childFoods.forEach(element => {temp += element.emissions; });
      return temp * numbers.ger_home_factor;
    }
  }

  public setTotalCalories(caloriesTotal: number) {
    this.totalCalories = caloriesTotal;
    if (this.hasChildFoods === true){
      this.childFoods.forEach(element => {
        element.setTotalCalories(caloriesTotal);
      });
    }
  }
}

export class QuestionOrder {
  private questionnaire: Questionnaire;
  public firstScreen: Questions = Questions.calories; // First screen of questionnaire
  public lastScreen: Questions = Questions.results_nutrition_emission_overview; // Last screen of questionnaire
  public firstQuestion: Questions = Questions.general_nutrition; // The first question which the user has to answer
  public lastQuestion: Questions = Questions.food_waste; // The last question which the user has to answer
  public get isFirstScreen(): boolean { return this.questionnaire.currentQuestion === this.firstScreen; }
  public get isLastScreen(): boolean { return this.questionnaire.currentQuestion === this.lastScreen; }
  public get isFirstQuestion(): boolean { return this.questionnaire.currentQuestion === this.firstQuestion; }
  public get isLastQuestion(): boolean { return this.questionnaire.currentQuestion === this.lastQuestion; }
  public get isQuestion(): boolean { return this.questionnaire.currentQuestion >= this.firstQuestion
      && this.questionnaire.currentQuestion <= this.lastQuestion; }
  public get isResults(): boolean { return this.questionnaire.currentQuestion >= Questions.results; }

  constructor(questionnaire: Questionnaire) {
    this.questionnaire = questionnaire;
  }

  // set the next question
  public nextQuestion() {
    switch (this.questionnaire.currentQuestion) {
      case Questions.calories: {
        this.questionnaire.currentQuestion = Questions.general_nutrition;
        break;
      }
      case Questions.general_nutrition: {
        switch (this.questionnaire.generalNutritionType) {
          case GeneralNutritionAnswers.vegan: {
            this.questionnaire.currentQuestion = Questions.nuts;
            break;
          }
          case GeneralNutritionAnswers.vegetarian: {
            this.questionnaire.currentQuestion = Questions.eggs;
            break;
          }
          case GeneralNutritionAnswers.mixed: {
            this.questionnaire.currentQuestion = Questions.meat;
            break;
          }
        }
        break;
      }
      case Questions.eggs: {
        this.questionnaire.currentQuestion = Questions.cheese;
        break;
      }
      case Questions.cheese: {
        this.questionnaire.currentQuestion = Questions.dairy;
        break;
      }
      case Questions.dairy: {
        this.questionnaire.currentQuestion = Questions.nuts;
        break;
      }
      case Questions.meat: {
        this.questionnaire.currentQuestion = Questions.fish;
        break;
      }
      case Questions.fish: {
        this.questionnaire.currentQuestion = Questions.eggs;
        break;
      }
      case Questions.nuts: {
        this.questionnaire.currentQuestion = Questions.frozen_food;
        break;
      }
      case Questions.frozen_food: {
        this.questionnaire.currentQuestion = Questions.sweets;
        break;
      }
      case Questions.sweets: {
        this.questionnaire.currentQuestion = Questions.food_waste;
        break;
      }
      case Questions.food_waste: {
        this.questionnaire.currentQuestion = Questions.finetuning;
        break;
      }
      case Questions.finetuning: {
        this.questionnaire.currentQuestion = Questions.results_compared_to_average;
        break;
      }
      case Questions.results_compared_to_average: {
        this.questionnaire.currentQuestion = Questions.results_nutrition_emission_overview;
        break;
      }
      case Questions.results_nutrition_emission_overview: {
        break;
      }
   }
  }

  // set the previous question
  public previousQuestion() {
    switch (this.questionnaire.currentQuestion) {
      case Questions.results_nutrition_emission_overview: {
        this.questionnaire.currentQuestion = Questions.results_compared_to_average;
        break;
      }
      case Questions.finetuning_detail: {
        this.questionnaire.currentQuestion = Questions.finetuning;
        break;
      }
      case Questions.results_compared_to_average: {
        this.questionnaire.currentQuestion = Questions.finetuning;
        break;
      }
      case Questions.finetuning: {
        this.questionnaire.currentQuestion = Questions.food_waste;
        break;
      }
      case Questions.food_waste: {
        this.questionnaire.currentQuestion = Questions.sweets;
        break;
      }
      case Questions.sweets: {
        this.questionnaire.currentQuestion = Questions.frozen_food;
        break;
      }
      case Questions.frozen_food: {
        this.questionnaire.currentQuestion = Questions.nuts;
        break;
      }
      case Questions.nuts: {
        switch (this.questionnaire.generalNutritionType) {
          case GeneralNutritionAnswers.vegan: {
            this.questionnaire.currentQuestion = Questions.general_nutrition;
            break;
          }
          default: {
            this.questionnaire.currentQuestion = Questions.dairy;
            break;
          }
        }
        break;
      }
      case Questions.dairy: {
        this.questionnaire.currentQuestion = Questions.cheese;
        break;
      }
      case Questions.cheese: {
        this.questionnaire.currentQuestion = Questions.eggs;
        break;
      }
      case Questions.eggs: {
        switch (this.questionnaire.generalNutritionType) {
          case GeneralNutritionAnswers.vegetarian: {
            this.questionnaire.currentQuestion = Questions.general_nutrition;
            break;
          }
          default: {
            this.questionnaire.currentQuestion = Questions.fish;
            break;
          }
        }
        break;
      }
      case Questions.fish: {
        this.questionnaire.currentQuestion = Questions.meat;
        break;
      }
      case Questions.meat: {
        this.questionnaire.currentQuestion = Questions.general_nutrition;
        break;
      }
      case Questions.general_nutrition: {
        this.questionnaire.currentQuestion = Questions.calories;
        break;
      }
      case Questions.calories: {
        this.questionnaire.currentQuestion = Questions.calories;
        break;
      }
    }
  }
}

export enum Questions {
  calories,
  general_nutrition,
  eggs,
  cheese,
  dairy,
  meat,
  fish,
  nuts,
  frozen_food,
  sweets,
  food_waste,
  finetuning,
  finetuning_detail,
  results,
  results_compared_to_average,
  results_nutrition_emission_overview
}

export enum Gender {
  female, male, diverse
}

export enum ActivityLevel {
  sittingOnly, mainlySitting, partlySitting, mainlyStanding, physicallyDemanding
}

export enum GeneralNutritionAnswers {
  vegan = 0, vegetarian = 1, mixed = 2
}

export enum StandardAnswers {
    never, rarely, nowAndThen, often, frequently
}

export enum FoodWasteAswers {
    rarely, nowAndThen, often
}

export enum QuestionnaireType {
  standard, tracker_details, onboarding
}

export enum QuestionnaireChangeType {
  miscellaneous,
  calories
}

export interface QuestionnaireChangeEvent {
  type: QuestionnaireChangeType;
  previousValue?: any;
  newValue?: any;
}

export interface IQuestionnaire {
  currentQuestion: Questions;
  gender: number;
  size: number;
  weight: number;
  age: number;
  activityLevel: number;
  generalNutritionType: number;
  eggsAnswer: number;
  cheeseAnswer: number;
  dairyAnswer: number;
  meatAnswer: number;
  fishAnswer: number;
  nutsAnswer: number;
  frozenFoodAnswer: number;
  sweetsAnswer: number;
  foodWasteAnswer: number;
  calories: number;
}

export interface Portion {
  amount: number;
  per: PortionUnits;
}

export enum PortionUnits {
  DAY,
  WEEK
}

export enum EmissionTarget {
  ONE_POINT_FIVE,
  TWO
}

export enum TargetProbability {
  UNLIKELY,
  MORE_THAN_33,
  MORE_THAN_50,
  MORE_THAN_67
}

export interface Question{
  text: string;
  options: string[];
  values: number[];
  questionConstant: Questions;
}
